/**
 * What every lit program reads of the screen-space ambient occlusion (`ssaoSceneChunk.ts`): the
 * occlusion of the render that draws, and how far it reaches into the direct light.
 */
import * as THREE from 'three';
import { installedSlot, showInSlot } from '../sharedTexture.js';
import { installedOr } from '../shaderPatch/litUniform.js';
import { componentReads, SCREEN_SPACE_STATE, screenSpaceState, } from '../screenSpaceEffects/screenSpaceState.js';
export const SSAO_UNIFORM = { occlusion: 'godotSsaoOcclusion', fold: 'godotSsaoFold' };
/**
 * The full-size occlusion a lit material reads at its own pixel, in Godot's rows. three never
 * uploads the slot and binds whatever handle it holds.
 */
export const occlusionSlot = installedSlot(SSAO_UNIFORM.occlusion, THREE.Texture);
/**
 * Written only by {@link showOcclusion}, and kept by reference in every material. Its components
 * are {@link SSAO_FOLD}'s.
 */
export const occlusionFold = installedOr(SSAO_UNIFORM.fold, Float32Array, () => new Float32Array(3));
/**
 * The indices of the components of {@link occlusionFold}: Godot's
 * `ssao_light_affect` and `ssao_ao_affect` (`render_forward_clustered.cpp:751-752`), and 1 while
 * the slot holds an occlusion. Godot binds its black default where none was drawn (`:3529-3530`).
 */
const SSAO_FOLD = { lightAffect: 0, aoChannelAffect: 1, hasOcclusion: 2 };
/** The GLSL read of each component of {@link occlusionFold}. */
export const SSAO_FOLD_READ = componentReads(SSAO_UNIFORM.fold, SSAO_FOLD);
/**
 * Shows `occlusion` to the lit programs that draw next, at the affects of `ssao`. A null
 * `occlusion` folds Godot's black default in.
 */
export function showOcclusion(renderer, occlusion, ssao) {
    if (occlusion)
        showInSlot(renderer, occlusionSlot, occlusion);
    occlusionFold[SSAO_FOLD.lightAffect] = ssao.lightAffect;
    occlusionFold[SSAO_FOLD.aoChannelAffect] = ssao.aoChannelAffect;
    occlusionFold[SSAO_FOLD.hasOcclusion] = occlusion ? 1 : 0;
    screenSpaceState[SCREEN_SPACE_STATE.showsOcclusion] = 1;
}
/** Shows no occlusion to the lit programs that draw next. */
export function hideOcclusion() {
    screenSpaceState[SCREEN_SPACE_STATE.showsOcclusion] = 0;
}
//# sourceMappingURL=ssaoUniforms.js.map